import { useEffect, useMemo, useState } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { useRoles } from "@/context/RolesContext";
import RoleBuilder, { CAP_GROUPS } from "@/components/RoleBuilder";
import { useDialog } from "@/context/DialogContext";
import BrandingTab from "@/components/BrandingTab";
import EmailTab from "@/components/EmailTab";
import ExpensesConfigTab from "@/components/ExpensesConfigTab";
import OnboardingConfigTab from "@/components/OnboardingConfigTab";
import OffboardingConfigTab from "@/components/OffboardingConfigTab";
import GeneralSettingsTab from "@/components/GeneralSettingsTab";
import PasswordPolicyTab from "@/components/PasswordPolicyTab";
import IdleTrackerConfigTab from "@/components/IdleTrackerConfigTab";
import ActivityLogsTab from "@/components/ActivityLogsTab";
import BackupTab from "@/components/BackupTab";
import SSOSettingsTab from "@/components/SSOSettingsTab";
import ReferralsConfigTab from "@/components/ReferralsConfigTab";
import JobTemplatesTab from "@/components/JobTemplatesTab";
import IssueTemplatesTab from "@/components/IssueTemplatesTab";
import TaskFormsTab from "@/components/TaskFormsTab";
import PerformanceReviewFormsTab from "@/components/PerformanceReviewFormsTab";
import LeaveSettingsTab from "@/components/LeaveSettingsTab";
import HolidaysTab from "@/components/HolidaysTab";
import {
  ShieldCheck, FloppyDisk, ArrowClockwise, CheckSquare, Square, Plus,
  PencilSimple, Trash, Lock, IdentificationBadge, Users, Clock, Palette,
  EnvelopeSimple, Wallet, Rocket, DoorOpen, Gear, ClipboardText, Database, Cloud,
  UserPlus, Briefcase, WarningCircle,
} from "@phosphor-icons/react";

// Legacy 4-role fixed colors — dynamic roles fall back to their `color` field.
const BUILT_IN_ROLE_BG = {
  admin: "bg-[#002FA7] text-white",
  manager: "bg-[#FF3B30] text-white",
  hr: "bg-[#7C3AED] text-white",
  employee: "bg-[#0A0A0A] text-white",
};
const roleBadge = (r) => {
  if (BUILT_IN_ROLE_BG[r.key]) return { cls: BUILT_IN_ROLE_BG[r.key], style: {} };
  return { cls: "text-white", style: { backgroundColor: r.color || "#0A0A0A" } };
};

// ============ ROLES TAB ============
function RolesTab({ allRoles, refreshRoles, users }) {
  const { confirm } = useDialog();
  const [editing, setEditing] = useState(null); // {mode:"create"|"edit", role?}
  const [msg, setMsg] = useState({ type: "", text: "" });

  const roleUserCount = useMemo(() => {
    const m = {};
    (users || []).forEach(u => {
      const rs = u.roles && u.roles.length ? u.roles : [u.role];
      rs.forEach(r => { m[r] = (m[r] || 0) + 1; });
    });
    return m;
  }, [users]);

  const sorted = useMemo(
    () => [...allRoles].sort((a, b) => (a.order ?? 100) - (b.order ?? 100) || a.label.localeCompare(b.label)),
    [allRoles]
  );

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const remove = async (r) => {
    if (r.protected) return;
    const ok = await confirm({
      title: `Delete role "${r.label}"?`,
      description: "This action cannot be undone. Users currently holding this role will need to be reassigned.",
      confirmText: "Delete role",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/roles/${r.key}`);
      await refreshRoles();
      flash("ok", `Role "${r.label}" deleted.`);
    } catch (e) {
      flash("error", formatAxiosError(e));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Define who can do what. Custom roles combine a set of capabilities with optional system flags.
            Built-in roles (admin, manager, hr, employee) cannot be deleted or renamed.
          </p>
        </div>
        <button
          onClick={() => setEditing({ mode: "create" })}
          className="btn-primary flex items-center gap-2"
          data-testid="create-role-button"
        >
          <Plus size={14} weight="bold" /> New role
        </button>
      </div>

      {msg.text && (
        <div className={`mb-4 px-4 py-2 text-sm border ${msg.type === "ok" ? "border-[#22C55E] bg-green-50 text-[#166534]" : "border-[#FF3B30] bg-red-50 text-[#FF3B30]"}`}>
          {msg.text}
        </div>
      )}

      <div className="border border-border" data-testid="roles-list">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-muted border-b border-border text-[10px] font-mono-mini uppercase">
          <div className="col-span-4">Role</div>
          <div className="col-span-3">Flags</div>
          <div className="col-span-1 text-right">Rank</div>
          <div className="col-span-2 text-right">Members</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {sorted.map(r => {
          const b = roleBadge(r);
          const capsCount = Object.values(r.capabilities || {}).filter(Boolean).length;
          const users = roleUserCount[r.key] || 0;
          return (
            <div key={r.key} className="grid grid-cols-12 gap-2 px-4 py-4 border-b border-border last:border-b-0 items-center" data-testid={`role-row-${r.key}`}>
              <div className="col-span-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`inline-block text-[11px] px-2 py-0.5 uppercase font-mono-mini ${b.cls}`} style={b.style}>
                    {r.label}
                  </span>
                  {r.protected && <Lock size={12} weight="bold" className="text-muted-foreground" />}
                  <span className="text-[10px] font-mono-mini text-muted-foreground">{r.key}</span>
                </div>
                <div className="text-xs text-muted-foreground">{r.description || <span className="italic">No description.</span>}</div>
                <div className="text-[10px] font-mono-mini text-muted-foreground mt-1">
                  {capsCount} capabilit{capsCount === 1 ? "y" : "ies"} enabled
                </div>
              </div>
              <div className="col-span-3 flex flex-col gap-1">
                {r.flags?.is_hr_approver && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono-mini uppercase text-[#7C3AED]" title="Appears in HR leave-approver pool">
                    <IdentificationBadge size={10} weight="bold" /> HR approver
                  </span>
                )}
                {r.flags?.is_manager_owner && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono-mini uppercase text-[#FF3B30]" title="Can own projects & approve team leave">
                    <Users size={10} weight="bold" /> Project owner
                  </span>
                )}
                {!r.flags?.is_hr_approver && !r.flags?.is_manager_owner && (
                  <span className="text-[10px] font-mono-mini text-muted-foreground">—</span>
                )}
              </div>
              <div className="col-span-1 text-right font-mono-mini text-sm">{r.rank}</div>
              <div className="col-span-2 text-right font-mono-mini text-sm">{users}</div>
              <div className="col-span-2 flex justify-end gap-1">
                <button
                  onClick={() => setEditing({ mode: "edit", role: r })}
                  className="p-2 hover:bg-muted"
                  data-testid={`edit-role-${r.key}`}
                  title="Edit"
                >
                  <PencilSimple size={14} weight="bold" />
                </button>
                <button
                  onClick={() => remove(r)}
                  disabled={r.protected || users > 0}
                  className="p-2 hover:bg-muted text-[#FF3B30] disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid={`delete-role-${r.key}`}
                  title={r.protected ? "Protected role — cannot delete" : users > 0 ? "Cannot delete — role in use" : "Delete"}
                >
                  <Trash size={14} weight="bold" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <RoleBuilder
          mode={editing.mode}
          role={editing.role}
          allRoles={allRoles}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            await refreshRoles();
            flash("ok", editing.mode === "create" ? "Role created." : "Role updated.");
          }}
        />
      )}
    </div>
  );
}

// ============ PERMISSIONS MATRIX TAB ============
function PermissionsMatrixTab({ allRoles, refreshRoles }) {
  const { refresh: refreshMyCaps } = useCapabilities();
  const [matrix, setMatrix] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const load = async () => {
    try {
      const r = await api.get("/settings/capabilities");
      setMatrix(r.data.matrix);
      setInitial(JSON.parse(JSON.stringify(r.data.matrix)));
    } catch (e) {
      setMsg({ type: "error", text: formatAxiosError(e) });
    }
  };
  useEffect(() => { load(); }, [allRoles.length]);

  const dirty = matrix && initial && JSON.stringify(matrix) !== JSON.stringify(initial);

  const toggle = (roleKey, cap) => {
    if (roleKey === "admin") return; // Admin always keeps everything
    setMatrix(m => ({ ...m, [roleKey]: { ...m[roleKey], [cap]: !m[roleKey]?.[cap] } }));
  };

  const save = async () => {
    setSaving(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.put("/settings/capabilities", { matrix });
      setMatrix(r.data.matrix);
      setInitial(JSON.parse(JSON.stringify(r.data.matrix)));
      setMsg({ type: "ok", text: "Permissions saved." });
      refreshMyCaps();
      refreshRoles();
      setTimeout(() => setMsg({ type: "", text: "" }), 3000);
    } catch (e) {
      setMsg({ type: "error", text: formatAxiosError(e) });
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setMatrix(JSON.parse(JSON.stringify(initial)));

  if (!matrix) {
    return <div className="p-8 text-sm text-muted-foreground">Loading permissions…</div>;
  }

  const sortedRoles = [...allRoles].sort((a, b) => (a.order ?? 100) - (b.order ?? 100) || a.label.localeCompare(b.label));

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Toggle what each role can do. Users with multiple roles get the union of their capabilities.
          Admin retains full access at all times.
        </p>
        <div className="flex items-center gap-2">
          {dirty && (
            <button onClick={reset} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2" data-testid="reset-caps">
              <ArrowClockwise size={14} weight="bold" /> Reset
            </button>
          )}
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="btn-primary flex items-center gap-2 disabled:opacity-40"
            data-testid="save-caps"
          >
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {msg.text && (
        <div className={`mb-6 px-4 py-2 text-sm border ${msg.type === "ok" ? "border-[#22C55E] bg-green-50 text-[#166534]" : "border-[#FF3B30] bg-red-50 text-[#FF3B30]"}`}>
          {msg.text}
        </div>
      )}

      {CAP_GROUPS.map(group => (
        <section key={group.title} className="mb-8" data-testid={`cap-group-${group.title.toLowerCase().replace(/[^a-z]/g, "-")}`}>
          <div className="eyebrow mb-3">{group.title}</div>
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr className="text-left">
                  <th className="px-4 py-3 eyebrow">Capability</th>
                  {sortedRoles.map(r => {
                    const b = roleBadge(r);
                    return (
                      <th key={r.key} className="px-4 py-3 text-center">
                        <span className={`inline-block text-[10px] px-1.5 py-0.5 font-mono-mini uppercase ${b.cls}`} style={b.style}>
                          {r.label}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {group.caps.map(([key, label]) => (
                  <tr key={key} className="border-t border-border" data-testid={`cap-row-${key}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-sm">{label}</div>
                      <div className="text-[10px] font-mono-mini text-muted-foreground">{key}</div>
                    </td>
                    {sortedRoles.map(r => {
                      const on = !!matrix[r.key]?.[key];
                      const isAdmin = r.key === "admin";
                      // For the admin column, manage_permissions & manage_roles are locked on.
                      const locked = isAdmin;
                      return (
                        <td key={r.key} className="px-4 py-3 text-center">
                          <button
                            onClick={() => toggle(r.key, key)}
                            disabled={locked}
                            className={`inline-flex items-center justify-center p-1 ${locked ? "opacity-60 cursor-not-allowed" : "hover:bg-muted"}`}
                            data-testid={`cap-toggle-${r.key}-${key}`}
                            title={locked ? "Admin cannot be limited" : (on ? "Enabled — click to disable" : "Disabled — click to enable")}
                          >
                            {on
                              ? <CheckSquare size={20} weight="fill" className="text-[#002FA7]" />
                              : <Square size={20} weight="bold" className="text-muted-foreground" />}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}

// ============ TIMESHEET CONFIG TAB ============
function TimesheetConfigTab({ allRoles }) {
  const [cfg, setCfg] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const load = async () => {
    try {
      const r = await api.get("/timesheets/config");
      setCfg(r.data);
      setInitial(r.data);
    } catch (e) {
      setMsg({ type: "error", text: formatAxiosError(e) });
    }
  };
  useEffect(() => { load(); }, []);

  const dirty = cfg && initial && JSON.stringify(cfg) !== JSON.stringify(initial);

  const save = async () => {
    setSaving(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.put("/timesheets/config", {
        default_approver_role: cfg.default_approver_role,
        max_hours_per_day: Number(cfg.max_hours_per_day),
      });
      setCfg(r.data); setInitial(r.data);
      setMsg({ type: "ok", text: "Saved." });
      setTimeout(() => setMsg({ type: "", text: "" }), 3000);
    } catch (e) {
      setMsg({ type: "error", text: formatAxiosError(e) });
    } finally { setSaving(false); }
  };

  if (!cfg) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const sortedRoles = [...allRoles].sort((a, b) => (a.order ?? 100) - (b.order ?? 100));

  return (
    <div className="max-w-2xl">
      <p className="text-sm text-muted-foreground mb-6">
        Global defaults for the timesheet module. Individual projects can override the approver role
        from the project&apos;s <b>Approver</b> settings on the Kanban page.
      </p>

      {msg.text && (
        <div className={`mb-4 px-4 py-2 text-sm border ${msg.type === "ok" ? "border-[#22C55E] bg-green-50 text-[#166534]" : "border-[#FF3B30] bg-red-50 text-[#FF3B30]"}`}>
          {msg.text}
        </div>
      )}

      <section className="mb-6">
        <div className="eyebrow mb-3">Default approver role</div>
        <div className="space-y-1" data-testid="ts-default-approver-list">
          {sortedRoles.map(r => (
            <label key={r.key} className={`flex items-center gap-3 border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted ${cfg.default_approver_role === r.key ? "bg-muted" : ""}`}>
              <input
                type="radio"
                name="approver_role"
                checked={cfg.default_approver_role === r.key}
                onChange={() => setCfg({ ...cfg, default_approver_role: r.key })}
                data-testid={`ts-cfg-role-${r.key}`}
              />
              <span
                className="inline-block text-[10px] px-1.5 py-0.5 uppercase font-mono-mini text-white"
                style={{ backgroundColor: r.color || "#0A0A0A" }}
              >
                {r.label}
              </span>
              {r.description && <span className="text-xs text-muted-foreground truncate">{r.description}</span>}
            </label>
          ))}
        </div>
        <div className="text-[10px] font-mono-mini text-muted-foreground mt-2">
          Timesheets that touch projects without a per-project override are routed to users with this role
          (who also hold the <b>approve_timesheets</b> capability).
        </div>
      </section>

      <section className="mb-6">
        <div className="eyebrow mb-3">Daily hours cap</div>
        <input
          type="number"
          min="1" max="24" step="0.5"
          value={cfg.max_hours_per_day}
          onChange={(e) => setCfg({ ...cfg, max_hours_per_day: e.target.value })}
          className="w-32 border border-border px-3 py-2 text-sm"
          data-testid="ts-cfg-max-hours"
        />
        <div className="text-[10px] font-mono-mini text-muted-foreground mt-2">
          Users cannot log more than this many total hours on any single day (across all projects).
        </div>
      </section>

      <div className="flex items-center gap-2">
        <button onClick={load} disabled={!dirty} className="border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-40">
          Reset
        </button>
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="btn-primary flex items-center gap-2 disabled:opacity-40"
          data-testid="save-ts-config"
        >
          <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

// ============ SETTINGS PAGE (TABBED) ============
export default function Settings() {
  const { roles: allRoles, refresh: refreshRoles, loaded } = useRoles();
  const { can } = useCapabilities();
  const [tab, setTab] = useState("general"); // "general" | "roles" | "permissions" | "timesheet" | ...
  const [users, setUsers] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/users").then(r => { if (alive) setUsers(r.data || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!loaded) {
    return <div className="p-12 text-sm text-muted-foreground">Loading…</div>;
  }

  const canManageRoles = can("manage_roles");
  const canManagePerms = can("manage_permissions");
  const canManageTs = can("manage_timesheet_config");
  const canManageBranding = can("manage_branding");
  const canManageEmail = can("manage_email");
  const canManageExpenses = can("manage_expenses_config");
  const canManageOnboarding = can("manage_onboarding_config");
  const canManageGeneral = can("manage_general_settings");
  const canViewAudit = can("view_audit_logs");
  const canManageBackups = can("manage_backups");
  const canManageSSO = can("manage_sso");
  const canManageReferrals = can("manage_referrals_config");
  const canManageJobTpl = can("manage_job_templates");
  const canManageIssueTpl = can("manage_issue_templates");
  const canManageIdleTracker = can("manage_idle_tracker_config");
  const canManageTaskForms = can("manage_task_forms");
  const canManagePerformanceForms = can("manage_performance_review_forms");
  const canManageLeaveSettings = can("manage_leave_settings");
  const canManageHolidays = can("manage_holidays");

  // If the current tab isn't visible to the user, fall back
  const visibleTab = (
    (tab === "general" && canManageGeneral) ||
    (tab === "roles" && canManageRoles) ||
    (tab === "permissions" && canManagePerms) ||
    (tab === "timesheet" && canManageTs) ||
    (tab === "branding" && canManageBranding) ||
    (tab === "email" && canManageEmail) ||
    (tab === "expenses" && canManageExpenses) ||
    (tab === "onboarding" && canManageOnboarding) ||
    (tab === "offboarding" && canManageOnboarding) ||
    (tab === "activity" && canViewAudit) ||
    (tab === "backup" && canManageBackups) ||
    (tab === "sso" && canManageSSO) ||
    (tab === "referrals" && canManageReferrals) ||
    (tab === "job_templates" && canManageJobTpl) ||
    (tab === "issue_templates" && canManageIssueTpl) ||
    (tab === "password_policy" && canManageGeneral) ||
    (tab === "task_forms" && canManageTaskForms) ||
    (tab === "idle_tracker" && canManageIdleTracker) ||
    (tab === "performance_forms" && canManagePerformanceForms) ||
    (tab === "leave_settings" && canManageLeaveSettings) ||
    (tab === "holidays" && canManageHolidays)
  ) ? tab : (canManageGeneral ? "general" : canManageRoles ? "roles" : canManagePerms ? "permissions" : canManageTs ? "timesheet" : canManageBranding ? "branding" : canManageEmail ? "email" : canManageExpenses ? "expenses" : canViewAudit ? "activity" : canManageBackups ? "backup" : canManageSSO ? "sso" : canManageReferrals ? "referrals" : canManageJobTpl ? "job_templates" : canManageIssueTpl ? "issue_templates" : canManageIdleTracker ? "idle_tracker" : canManageHolidays ? "holidays" : "onboarding");

  const titles = { general: "General", roles: "Roles", permissions: "Permissions", timesheet: "Timesheet", branding: "Branding", email: "Email", expenses: "Expenses", onboarding: "Onboarding", offboarding: "Offboarding", activity: "Activity Logs", backup: "Backup & Restore", sso: "Single Sign-On (SSO)", referrals: "Referrals Config", job_templates: "Job Templates", issue_templates: "Issue Templates", password_policy: "Password Policy", idle_tracker: "Idle Tracker", task_forms: "Task Forms", performance_forms: "Performance Forms", leave_settings: "Leave Management", holidays: "Holidays" };

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-8">
        <div className="eyebrow mb-2 flex items-center gap-2">
          <ShieldCheck size={12} weight="bold" /> Governance / Settings
        </div>
        <h1 className="font-display text-4xl font-black tracking-tight">
          {titles[visibleTab]}
        </h1>

        <div className="mt-6 flex items-center gap-1 border-b border-border -mb-8 flex-wrap">
          {canManageGeneral && (
            <button
              onClick={() => setTab("general")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "general" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-general"
            >
              <Gear size={12} weight="bold" /> General
            </button>
          )}
          {canManageRoles && (
            <button
              onClick={() => setTab("roles")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px ${visibleTab === "roles" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-roles"
            >
              Roles ({allRoles.length})
            </button>
          )}
          {canManagePerms && (
            <button
              onClick={() => setTab("permissions")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px ${visibleTab === "permissions" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-permissions"
            >
              Permissions matrix
            </button>
          )}
          {canManageTs && (
            <button
              onClick={() => setTab("timesheet")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "timesheet" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-timesheet"
            >
              <Clock size={12} weight="bold" /> Timesheet
            </button>
          )}
          {canManageBranding && (
            <button
              onClick={() => setTab("branding")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "branding" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-branding"
            >
              <Palette size={12} weight="bold" /> Branding
            </button>
          )}
          {canManageEmail && (
            <button
              onClick={() => setTab("email")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "email" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-email"
            >
              <EnvelopeSimple size={12} weight="bold" /> Email
            </button>
          )}
          {canManageExpenses && (
            <button
              onClick={() => setTab("expenses")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "expenses" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-expenses-config"
            >
              <Wallet size={12} weight="bold" /> Expenses
            </button>
          )}
          {canManageOnboarding && (
            <button
              onClick={() => setTab("onboarding")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "onboarding" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-onboarding-config"
            >
              <Rocket size={12} weight="bold" /> Onboarding
            </button>
          )}
          {canManageOnboarding && (
            <button
              onClick={() => setTab("offboarding")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "offboarding" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-offboarding-config"
            >
              <DoorOpen size={12} weight="bold" /> Offboarding
            </button>
          )}
          {canViewAudit && (
            <button
              onClick={() => setTab("activity")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "activity" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-activity"
            >
              <ClipboardText size={12} weight="bold" /> Activity Logs
            </button>
          )}
          {canManageBackups && (
            <button
              onClick={() => setTab("backup")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "backup" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-backup"
            >
              <Database size={12} weight="bold" /> Backup
            </button>
          )}
          {canManageSSO && (
            <button
              onClick={() => setTab("sso")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "sso" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-sso"
            >
              <Cloud size={12} weight="bold" /> SSO
            </button>
          )}
          {canManageReferrals && (
            <button
              onClick={() => setTab("referrals")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "referrals" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-referrals-config"
            >
              <UserPlus size={12} weight="bold" /> Referrals
            </button>
          )}
          {canManageJobTpl && (
            <button
              onClick={() => setTab("job_templates")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "job_templates" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-job-templates"
            >
              <Briefcase size={12} weight="bold" /> Job Templates
            </button>
          )}
          {canManageIssueTpl && (
            <button
              onClick={() => setTab("issue_templates")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "issue_templates" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-issue-templates"
            >
              <WarningCircle size={12} weight="bold" /> Issue Templates
            </button>
          )}
          {canManageGeneral && (
            <button
              onClick={() => setTab("password_policy")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "password_policy" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-password-policy"
            >
              🔒 Password
            </button>
          )}
          {canManageTaskForms && (
            <button
              onClick={() => setTab("task_forms")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "task_forms" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-task-forms"
            >
              📋 Task Forms
            </button>
          )}
          {canManageIdleTracker && (
            <button
              onClick={() => setTab("idle_tracker")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "idle_tracker" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-idle-tracker"
            >
              👁 Idle Tracker
            </button>
          )}
          {canManagePerformanceForms && (
            <button
              onClick={() => setTab("performance_forms")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "performance_forms" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-performance-forms"
            >
              📊 Performance Forms
            </button>
          )}
          {canManageLeaveSettings && (
            <button
              onClick={() => setTab("leave_settings")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "leave_settings" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-leave-settings"
            >
              🏖 Leave Settings
            </button>
          )}
          {canManageHolidays && (
            <button
              onClick={() => setTab("holidays")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${visibleTab === "holidays" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-holidays"
            >
              🎉 Holidays
            </button>
          )}
        </div>
      </div>

      <div className="p-8 lg:p-12">
        {visibleTab === "general" && <GeneralSettingsTab />}
        {visibleTab === "roles" && <RolesTab allRoles={allRoles} refreshRoles={refreshRoles} users={users} />}
        {visibleTab === "permissions" && <PermissionsMatrixTab allRoles={allRoles} refreshRoles={refreshRoles} />}
        {visibleTab === "timesheet" && <TimesheetConfigTab allRoles={allRoles} />}
        {visibleTab === "branding" && <BrandingTab />}
        {visibleTab === "email" && <EmailTab />}
        {visibleTab === "expenses" && <ExpensesConfigTab />}
        {visibleTab === "onboarding" && <OnboardingConfigTab />}
        {visibleTab === "offboarding" && <OffboardingConfigTab />}
        {visibleTab === "activity" && <ActivityLogsTab />}
        {visibleTab === "backup" && <BackupTab />}
        {visibleTab === "sso" && <SSOSettingsTab />}
        {visibleTab === "referrals" && <ReferralsConfigTab />}
        {visibleTab === "job_templates" && <JobTemplatesTab />}
        {visibleTab === "issue_templates" && <IssueTemplatesTab />}
        {visibleTab === "password_policy" && <PasswordPolicyTab />}
        {visibleTab === "task_forms" && <TaskFormsTab />}
        {visibleTab === "idle_tracker" && <IdleTrackerConfigTab />}
        {visibleTab === "performance_forms" && <PerformanceReviewFormsTab />}
        {visibleTab === "leave_settings" && <LeaveSettingsTab />}
        {visibleTab === "holidays" && <HolidaysTab />}
      </div>
    </div>
  );
}
